import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  ChevronRight, 
  Search, 
  Menu, 
  X, 
  Home,
  Users,
  Calendar,
  Clock,
  FileText,
  Settings,
  CreditCard,
  CalendarDays,
  BarChart3,
  ArrowLeft,
  ExternalLink,
  Check,
  Play,
  MousePointer,
  GripHorizontal,
  User,
  PartyPopper
} from "lucide-react";
import { RotalieLogo } from "../components/RotalieLogo";
import { cn } from "../lib/utils";

// Documentation sections
const docSections = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: Home,
    articles: [
      { id: "introduction", title: "Introduction to Rotalie" },
      { id: "signing-up", title: "Creating Your Account" },
      { id: "dashboard-overview", title: "Dashboard Overview" },
    ]
  },
  {
    id: "employees",
    title: "Managing Employees",
    icon: Users,
    articles: [
      { id: "adding-employees", title: "Adding Employees" },
      { id: "editing-employees", title: "Editing Employee Details" },
      { id: "employee-pay-rates", title: "Pay Rates & Contracted Hours" },
    ]
  },
  {
    id: "scheduling",
    title: "Creating Schedules",
    icon: Calendar,
    articles: [
      { id: "day-view", title: "Day View" },
      { id: "week-view", title: "Week View" },
      { id: "creating-shifts", title: "Creating Shifts" },
      { id: "drag-to-create", title: "Drag to Create Shifts" },
      { id: "editing-shifts", title: "Editing & Deleting Shifts" },
    ]
  },
  {
    id: "shift-patterns",
    title: "Shift Patterns",
    icon: CalendarDays,
    articles: [
      { id: "about-patterns", title: "About Predetermined Shifts" },
      { id: "creating-patterns", title: "Creating Shift Patterns" },
      { id: "using-patterns", title: "Applying Patterns to Shifts" },
    ]
  },
  {
    id: "breaks",
    title: "Break Management",
    icon: Clock,
    articles: [
      { id: "break-types", title: "Setting Up Break Types" },
      { id: "required-breaks", title: "Required Breaks" },
      { id: "adding-breaks", title: "Adding Breaks to Shifts" },
    ]
  },
  {
    id: "absences",
    title: "Absence Tracking",
    icon: CalendarDays,
    articles: [
      { id: "recording-absences", title: "Recording Absences" },
      { id: "absence-types", title: "Absence Types" },
      { id: "paid-absences", title: "Paid Leave Calculations" },
    ]
  },
  {
    id: "events",
    title: "Events Management",
    icon: PartyPopper,
    articles: [
      { id: "about-events", title: "About Events" },
      { id: "creating-events", title: "Creating Events" },
      { id: "events-on-timeline", title: "Events on the Timeline" },
      { id: "events-reports", title: "Events in Reports" },
    ]
  },
  {
    id: "reports",
    title: "Reports & Analytics",
    icon: BarChart3,
    articles: [
      { id: "viewing-reports", title: "Viewing Reports" },
      { id: "exporting-data", title: "Exporting Data" },
      { id: "cost-tracking", title: "Cost Tracking" },
    ]
  },
  {
    id: "settings",
    title: "Settings",
    icon: Settings,
    articles: [
      { id: "business-hours", title: "Business Hours" },
      { id: "currency-settings", title: "Currency & Localization" },
      { id: "snap-intervals", title: "Time Snap Intervals" },
    ]
  },
  {
    id: "billing",
    title: "Billing & Subscriptions",
    icon: CreditCard,
    articles: [
      { id: "subscription-plans", title: "Subscription Plans" },
      { id: "managing-subscription", title: "Managing Your Subscription" },
      { id: "invoices", title: "Invoices & Payment History" },
    ]
  },
  {
    id: "account",
    title: "Account Settings",
    icon: User,
    articles: [
      { id: "my-account", title: "My Account" },
      { id: "company-details", title: "Company Details" },
      { id: "change-password", title: "Changing Your Password" },
    ]
  },
];

// Documentation content
const docContent = {
  // Getting Started
  "introduction": {
    title: "Introduction to Rotalie",
    content: `
# Welcome to Rotalie

Rotalie is a powerful staff scheduling platform designed to help businesses create, manage, and optimize their employee rotas with ease.

## Key Features

- **Visual Rota Builder** - Create schedules using our intuitive drag-and-drop interface
- **Employee Management** - Track pay rates, contracted hours, and employee details
- **Break Management** - Configure break types with automatic calculations
- **Real-Time Cost Tracking** - See labor costs as you build schedules
- **Absence Tracking** - Record holidays, sick leave, and other absences
- **Events Management** - Plan staffing around parties, meetings, and special occasions
- **Reports & Exports** - Generate detailed reports in CSV or PDF format

## Who is Rotalie for?

Rotalie is perfect for:
- Restaurants & Cafés
- Retail stores
- Healthcare facilities
- Hospitality businesses
- Any business with shift-based staff

## Getting Help

If you need assistance, our support team is here to help. You can reach us through the in-app support or by emailing support@rotalie.io.
    `
  },
  "signing-up": {
    title: "Creating Your Account",
    content: `
# Creating Your Account

Getting started with Rotalie takes just a few minutes. Follow these steps to create your account.

## Step 1: Visit the Signup Page

Navigate to [rotalie.io/signup](/signup) and you'll see our registration form.

## Step 2: Enter Company Details

Fill in your company information:
- **Company Name** - Your business name
- **Country** - Select your country (this helps us set the right currency)
- **Admin Email** - Your email address (you'll use this to log in)

## Step 3: Verify Your Email

We'll send a 6-digit verification code to your email. Enter this code to verify your account.

## Step 4: Create Your Password

Choose a secure password for your account. We recommend:
- At least 8 characters
- A mix of letters, numbers, and symbols

## Your Free Trial

When you sign up, you'll automatically get a **30-day free trial** with full access to all features. No credit card required!

After your trial, you can choose a plan that fits your needs.
    `
  },
  "dashboard-overview": {
    title: "Dashboard Overview",
    image: "/docs-images/dashboard-day-view.png",
    content: `
# Dashboard Overview

When you log in to Rotalie, you'll land on the main dashboard. Here's a quick tour of what you'll see.

![Dashboard Day View](/docs-images/dashboard-day-view.png)

## Navigation Bar

At the top of the screen, you'll find:
- **Rotalie Logo** - Click to return to the dashboard
- **Navigation Links** - Quick access to Rota, Employees, Absences, Reports, Billing, and Settings
- **Theme Toggle** - Switch between light and dark mode
- **Account Menu** - Access your profile and logout

## Day View (Default)

The main dashboard shows the **Day View** of your rota:
- **Date Navigation** - Move between days using the arrows
- **Today Button** - Jump back to today's date
- **Employee Rows** - Each employee has their own row
- **Timeline** - Visual timeline showing business hours
- **Shift Blocks** - Colored blocks represent scheduled shifts

## Quick Stats

At the bottom of the day view, you'll see summary statistics:
- **Shifts Today** - Number of shifts scheduled
- **Working Today** - Number of employees working
- **Total Hours** - Combined hours for the day
- **Total Cost** - Estimated labor cost

## View Toggle

Switch between Day View and Week View using the toggle buttons in the navigation area.
    `
  },

  // Employees
  "adding-employees": {
    title: "Adding Employees",
    image: "/docs-images/employees-page.png",
    content: `
# Adding Employees

Before you can create shifts, you need to add your employees to Rotalie.

![Employees Page](/docs-images/employees-page.png)

## How to Add an Employee

1. Click **Employees** in the navigation bar
2. Click the **Add Employee** button
3. Fill in the employee details:
   - **Name** - Employee's full name
   - **Email** - Their email address (optional)
   - **Role/Position** - e.g., Server, Chef, Manager
   - **Hourly Rate** - Their pay rate per hour
   - **Pay Type** - Hourly or Salaried
   - **Color** - Choose a color for their shifts

4. Click **Save** to add the employee

## Employee Limits

The number of employees you can add depends on your subscription plan:
- **Light** - Up to 10 employees
- **Standard** - Up to 25 employees
- **Professional** - Unlimited employees

Need more? You can purchase additional employee packs in the Billing section.
    `
  },
  "editing-employees": {
    title: "Editing Employee Details",
    content: `
# Editing Employee Details

You can update employee information at any time.

## How to Edit an Employee

1. Go to the **Employees** page
2. Find the employee you want to edit
3. Click the **Edit** button (pencil icon)
4. Update the details you need to change
5. Click **Save** to confirm your changes

## What Can You Edit?

- Name and email
- Role/position
- Hourly rate
- Pay type
- Contracted hours
- Minimum hours
- Color

## Deleting an Employee

To remove an employee:
1. Click the **Delete** button (trash icon)
2. Confirm the deletion

**Note:** Deleting an employee will also remove their scheduled shifts. Consider archiving instead if you need to keep historical data.
    `
  },
  "employee-pay-rates": {
    title: "Pay Rates & Contracted Hours",
    content: `
# Pay Rates & Contracted Hours

Rotalie helps you track employee compensation and working hour commitments.

## Setting Pay Rates

Each employee can have:
- **Base Hourly Rate** - Standard pay per hour
- **Additional Rates** - For overtime, weekends, nights, etc.

### Adding Multiple Pay Rates

1. Edit the employee
2. Scroll to the **Additional Pay Rates** section
3. Click **Add Rate**
4. Enter the rate name (e.g., "Weekend", "Night")
5. Set the hourly amount

## Contracted Hours

Set the number of hours an employee is contracted to work:
- **Contracted Hours** - Weekly guaranteed hours
- **Minimum Hours** - Minimum hours per week

The Week View will show warnings if an employee is scheduled under their contracted hours.

## Cost Calculations

Rotalie automatically calculates:
- Per-shift costs
- Daily totals
- Weekly totals

All calculations update in real-time as you build your schedule.
    `
  },

  // Scheduling
  "day-view": {
    title: "Day View",
    image: "/docs-images/dashboard-day-view.png",
    content: `
# Day View

The Day View provides a detailed look at a single day's schedule.

![Day View](/docs-images/dashboard-day-view.png)

## Understanding the Layout

- **Left Column** - Employee names with their photos/initials
- **Timeline** - Horizontal timeline showing your business hours
- **Shift Blocks** - Colored blocks representing scheduled shifts
- **Stats Column** - Hours and cost per employee (toggleable)

## Navigation

- **← → Arrows** - Move to previous/next day
- **Today Button** - Jump to current date
- **Date Picker** - Click the date to open a calendar

## Timeline Features

- **Hour Markers** - Vertical lines at each hour
- **Tick Marks** - Smaller marks at 15/30 minute intervals
- **Grid Lines** - Light background lines for easy alignment

## Business Hours

The timeline only shows your configured business hours. If you're closed on a particular day, you'll see a "Closed" message.

## Quick Actions

- **Click** on empty timeline space to add a shift
- **Drag** across the timeline to create a shift with specific times
- **Click** on a shift to edit it
- **Drag** shift edges to resize
    `
  },
  "week-view": {
    title: "Week View",
    image: "/docs-images/dashboard-week-view.png",
    content: `
# Week View

The Week View shows an entire week at a glance, perfect for planning ahead.

![Week View](/docs-images/dashboard-week-view.png)

## Layout Overview

- **Columns** - One column per day (Mon-Sun)
- **Rows** - One row per employee
- **Cells** - Show shift count, total hours, and cost

## Reading the Week View

Each cell shows:
- **Number of shifts** for that day
- **Total hours** scheduled
- Click any cell to see shift details

## Weekly Totals

The rightmost column shows weekly totals:
- Total hours per employee
- Warning indicators if under contracted hours

## Contracted Hours Warnings

If an employee is scheduled below their contracted hours, you'll see a warning:
- **Yellow** - Slightly under
- **Red** - Significantly under

## Quick Actions

- Click any cell to view that day's shifts
- Click **Add Shift** in a cell to create a new shift
- Use the Week View for capacity planning
    `
  },
  "creating-shifts": {
    title: "Creating Shifts",
    image: "/docs-images/add-shift-dialog.png",
    content: `
# Creating Shifts

There are multiple ways to create shifts in Rotalie.

![Add Shift Dialog](/docs-images/add-shift-dialog.png)

## Method 1: Click to Add

1. Navigate to the **Day View**
2. Find the employee's row
3. **Click** anywhere on their timeline
4. The Add Shift dialog opens with the clicked time pre-filled
5. Adjust start/end times if needed
6. Click **Add Shift**

## Method 2: Drag to Create (Quick Method)

1. Navigate to the **Day View**
2. Find the employee's row
3. **Click and drag** across the timeline
4. Release to instantly create the shift
5. No dialog needed - shift is created immediately!

## Method 3: From Week View

1. Go to the **Week View**
2. Click on a day cell
3. Click **Add Shift**
4. Fill in the shift details
5. Click **Save**

## Using Shift Patterns

If you have predetermined shifts set up:
1. Open the Add Shift dialog
2. Select a pattern from the dropdown
3. Times and details auto-fill
4. Click **Add Shift**

## Tips

- Shifts snap to your configured time intervals (default: 15 minutes)
- You'll see warnings if shifts overlap or are outside business hours
- The estimated cost is shown before you save
    `
  },
  "drag-to-create": {
    title: "Drag to Create Shifts",
    content: `
# Drag to Create Shifts

The fastest way to create shifts is by dragging directly on the timeline.

## How It Works

1. **Position** your cursor at the shift start time on an employee's row
2. **Click and hold** the mouse button
3. **Drag** to the desired end time
4. **Release** - the shift is created instantly!

## Visual Feedback

While dragging, you'll see:
- A **preview block** showing the shift being created
- **Time labels** displaying start and end times
- The block is colored with the employee's assigned color

## Benefits of Drag to Create

- **Faster** - No dialog boxes or forms to fill
- **Visual** - See exactly where the shift will appear
- **Intuitive** - Just like drawing on a calendar
- **Precise** - Times snap to your interval settings

## When the Dialog Appears

The shift dialog will only appear if:
- A **required break** needs to be scheduled (shifts over a certain length)
- Otherwise, the shift saves automatically

## Tips

- Drag in either direction (left-to-right or right-to-left)
- The shift will snap to your time interval (e.g., 15 minutes)
- Click on the created shift to edit details or add breaks
    `
  },
  "editing-shifts": {
    title: "Editing & Deleting Shifts",
    content: `
# Editing & Deleting Shifts

Easily modify or remove shifts as your schedule changes.

## Editing a Shift

### From Day View
1. **Click** on the shift block
2. The Edit Shift dialog opens
3. Modify the details:
   - Start/End times
   - Breaks
   - Pay rate
4. Click **Save Changes**

### Quick Resize
1. Hover over a shift block
2. **Drag** the left or right edge
3. Release to resize the shift

### Quick Move
1. **Drag** the center of a shift block
2. Move it to a new time
3. Release to confirm

## Deleting a Shift

1. Click on the shift to open the edit dialog
2. Click the **Delete** button (trash icon)
3. Confirm the deletion

## Locked Shifts

Shifts on past dates may be "locked" to prevent accidental changes. Locked shifts show a lock icon and cannot be edited.

## Bulk Actions

Currently, shifts are managed individually. Bulk editing features are planned for future updates.
    `
  },

  // Shift Patterns
  "about-patterns": {
    title: "About Predetermined Shifts",
    content: `
# About Predetermined Shifts

Shift patterns (predetermined shifts) let you save commonly-used shift templates for quick reuse.

## What Are Shift Patterns?

Shift patterns are pre-configured shift templates that include:
- Start and end times
- Break schedules
- A descriptive name

## Benefits

- **Save time** - Don't re-enter the same shift details
- **Consistency** - Ensure shifts follow standard patterns
- **Reduce errors** - Avoid typos in times

## Examples

Common shift patterns might include:
- **Morning Shift** - 6:00 AM to 2:00 PM
- **Evening Shift** - 2:00 PM to 10:00 PM
- **Night Shift** - 10:00 PM to 6:00 AM
- **Split Shift** - 11:00 AM to 2:00 PM, 5:00 PM to 9:00 PM

## Enabling Shift Patterns

1. Go to **Settings**
2. Find **Predetermined Shifts**
3. Toggle the switch to **Enable**
4. The **Shifts** tab will appear in navigation
    `
  },
  "creating-patterns": {
    title: "Creating Shift Patterns",
    content: `
# Creating Shift Patterns

Set up your shift patterns once, then use them whenever you create shifts.

## How to Create a Pattern

1. Enable Shift Patterns in Settings (if not already)
2. Click the **Shifts** tab in navigation
3. Click **Add Shift Pattern**
4. Enter the pattern details:
   - **Name** - e.g., "Morning Shift"
   - **Start Time** - When the shift begins
   - **End Time** - When the shift ends
   - **Breaks** - Optional break schedule
5. Click **Save**

## Managing Patterns

- **Edit** - Click the edit button to modify a pattern
- **Delete** - Remove patterns you no longer need
- **Reorder** - Drag patterns to change their order

## Tips

- Create patterns for your most common shift types
- Include typical breaks in the pattern
- Use clear, descriptive names
    `
  },
  "using-patterns": {
    title: "Applying Patterns to Shifts",
    content: `
# Applying Patterns to Shifts

Once you've created shift patterns, using them is easy.

## When Creating a New Shift

1. Open the Add Shift dialog (click on timeline)
2. Look for the **Pattern** dropdown at the top
3. Select a pattern
4. Times and breaks auto-fill
5. Adjust if needed
6. Click **Add Shift**

## What Gets Applied

When you select a pattern:
- Start time
- End time
- Any configured breaks

## Customizing After Selection

You can still customize after selecting a pattern:
- Change the times
- Add or remove breaks
- Modify any details

The pattern is just a starting point - you have full control over the final shift.
    `
  },

  // Breaks
  "break-types": {
    title: "Setting Up Break Types",
    content: `
# Setting Up Break Types

Configure different break types to match your business needs.

## Accessing Break Settings

1. Go to **Settings**
2. Scroll to the **Break Types** section

## Creating a Break Type

1. Click **Add Break Type**
2. Fill in the details:
   - **Name** - e.g., "Lunch Break"
   - **Duration** - Length in minutes
   - **Is Paid** - Whether break time is paid
   - **Icon** - Visual identifier

## Break Type Options

- **Coffee Break** - Short 15-minute breaks
- **Lunch Break** - Standard 30-60 minute meal breaks
- **Rest Break** - Required rest periods

## Paid vs Unpaid Breaks

- **Paid breaks** - Included in worked hours and cost calculations
- **Unpaid breaks** - Deducted from shift duration

## Default Break Types

Rotalie comes with common break types pre-configured. You can edit or delete these as needed.
    `
  },
  "required-breaks": {
    title: "Required Breaks",
    content: `
# Required Breaks

Ensure compliance with labor laws by setting up required breaks.

## What Are Required Breaks?

Required breaks are automatically enforced when a shift exceeds a certain duration. For example:
- Shifts over 6 hours must include a 30-minute break

## Setting Up Required Breaks

1. Edit a break type
2. Enable **Is Required**
3. Set **Minimum Shift Hours** - The threshold that triggers the requirement
4. Save changes

## How It Works

When creating or editing a shift:
- If duration exceeds the threshold, the break is automatically added
- A warning appears if required breaks are missing
- The shift cannot be saved without required breaks

## Example

If you set:
- Break Type: "Lunch Break" (30 min)
- Is Required: Yes
- Minimum Shift Hours: 6

Then any shift over 6 hours will require a 30-minute lunch break.
    `
  },
  "adding-breaks": {
    title: "Adding Breaks to Shifts",
    content: `
# Adding Breaks to Shifts

Add breaks to shifts for accurate time and cost tracking.

## Adding a Break

1. Open the shift edit dialog
2. Scroll to the **Breaks** section
3. Click **Add Break**
4. Select a break type
5. Adjust the start time if needed
6. The end time calculates automatically from duration

## Editing Break Times

- Change the **Start Time** dropdown
- The end time updates based on the break duration
- Ensure breaks fall within the shift times

## Removing Breaks

- Click the **X** button next to a break
- **Note:** Required breaks cannot be removed if the shift meets the threshold

## Break Indicators

On the timeline, breaks appear as:
- Hatched/striped sections within the shift block
- Different visual style from worked time

## Cost Impact

- **Paid breaks** - No change to shift cost
- **Unpaid breaks** - Reduces the billable hours and cost
    `
  },

  // Absences
  "recording-absences": {
    title: "Recording Absences",
    content: `
# Recording Absences

Track employee absences including holidays, sick leave, and other time off.

## Accessing Absences

Click **Absences** in the navigation bar to view the absence calendar.

## Recording an Absence

1. Go to the **Absences** page
2. Click **Add Absence**
3. Select the **Employee**
4. Choose the **Absence Type**
5. Set the **Date Range**
6. Add optional notes
7. Click **Save**

## Viewing Absences

The absence calendar shows:
- Color-coded blocks for different absence types
- Employee names with their scheduled absences
- Date range overview

## On the Rota

When an employee has an absence:
- Their row shows "Holiday", "Sick", etc.
- You cannot add shifts on absence days
- The cell is grayed out
    `
  },
  "absence-types": {
    title: "Absence Types",
    content: `
# Absence Types

Rotalie supports various absence types out of the box.

## Default Absence Types

- **Holiday** - Planned vacation time
- **Sick** - Illness-related absence
- **Personal** - Personal days
- **Training** - Professional development
- **Other** - Miscellaneous absences

## Paid vs Unpaid Absences

Each absence type can be:
- **Paid** - Employee receives their normal pay
- **Unpaid** - No pay for absence period

## How Paid Absences Work

For paid absences, Rotalie calculates:
- Daily pay based on contracted/minimum hours
- Added to cost reports

## Customizing Absence Types

Currently, absence types are pre-configured. Custom absence types are planned for future updates.
    `
  },
  "paid-absences": {
    title: "Paid Leave Calculations",
    content: `
# Paid Leave Calculations

Understand how Rotalie calculates paid absence costs.

## Calculation Method

Paid absences are calculated using:
1. Employee's hourly rate
2. Their contracted hours OR minimum hours
3. Number of absence days

## Formula

\`Daily Cost = Hourly Rate × (Contracted Hours ÷ 5)\`

This assumes a 5-day work week. The daily amount is multiplied by the number of absence days.

## Example

If an employee has:
- Hourly rate: £12.00
- Contracted hours: 40 per week
- 2 days paid holiday

Calculation:
- Daily hours: 40 ÷ 5 = 8 hours
- Daily cost: 8 × £12.00 = £96.00
- Total: 2 × £96.00 = £192.00

## In Reports

Paid absence costs appear in:
- Weekly summaries
- Monthly reports
- Cost breakdowns
    `
  },

  // Events Management
  "about-events": {
    title: "About Events",
    content: `
# About Events

Events in Rotalie help you plan staffing around important occasions like parties, meetings, conferences, and special bookings.

## What are Events?

Events are time-blocked occasions that appear on your rota timeline, helping you visualize when extra staffing might be needed.

## Key Features

- **Visual Timeline Display** - Events appear in a dedicated row above employee shifts
- **Color Coding** - Choose from 8 colors to categorize different event types
- **Time Tracking** - Track total event hours in reports
- **Notes** - Add details about each event

## Use Cases

Events are perfect for:
- **Party Bookings** - Track private events at your venue
- **Team Meetings** - Schedule recurring team gatherings
- **Special Occasions** - Mark holidays, anniversaries, or promotions
- **Conferences** - Plan for high-traffic periods
- **Training Sessions** - Allocate time for staff development

## Feature Availability

Events Management is available on **Standard** and **Professional** plans.
    `
  },

  "creating-events": {
    title: "Creating Events",
    content: `
# Creating Events

Learn how to add events to your Rotalie calendar.

## Accessing Events

Click **Events** in the navigation bar to open the Events management page.

## Creating a New Event

1. Click the **Add Event** button
2. Fill in the event details:
   - **Event Name** - A descriptive title (e.g., "Birthday Party", "Team Meeting")
   - **Color** - Choose from 8 color options to categorize your events
   - **Date** - Select the event date
   - **Start Time** - When the event begins
   - **End Time** - When the event ends
   - **Notes** - Optional additional details

3. Click **Create Event** to save

## Editing Events

To modify an existing event:
1. Find the event in the events list
2. Click the **Edit** button (pencil icon)
3. Update the details
4. Click **Update Event**

## Deleting Events

To remove an event:
1. Click the **Delete** button (trash icon)
2. Confirm the deletion

## Event Colors

Use colors to categorize events:
- **Blue** - General meetings
- **Green** - Private bookings
- **Purple** - Training sessions
- **Red** - Important events
- **Orange** - Maintenance/setup
- **Yellow** - Tentative bookings
- **Teal** - External events
- **Pink** - Celebrations
    `
  },

  "events-on-timeline": {
    title: "Events on the Timeline",
    content: `
# Events on the Timeline

Events are displayed directly on your rota for easy visualization.

## Day View

In Day View, events appear in a dedicated **Events row** above employee shifts:
- Events show as colored blocks spanning their duration
- The event name is displayed within the block
- Click any event to see its full details in a popover

## Week View

In Week View, events appear in an **Events row** showing all events for each day:
- Each day cell shows event badges
- If there are many events, a "+N" indicator shows how many more
- Click any event badge to view details

## Overlapping Events

When events overlap in time:
- They automatically stack into multiple rows
- All events remain visible and clickable
- The Events section expands to fit all events

## Using Events for Scheduling

Events help you make better scheduling decisions:
1. **See at a glance** which days have special events
2. **Plan staffing** based on expected busy periods
3. **Communicate** with staff about upcoming events
    `
  },

  "events-reports": {
    title: "Events in Reports",
    content: `
# Events in Reports

Track and analyze your events through Rotalie's reporting features.

## Events Summary Card

On the Reports page, the **Events** summary card shows:
- Total number of events for the selected period
- Quick overview at a glance

## Events Summary Tab

Click the **Events** tab in Reports to see detailed breakdown:

### Grouped by Event Name
When grouped by "Employee" (default):
- Event name with color indicator
- Number of occurrences
- Total hours for that event type

Example: "Birthday Party - 3 occurrences - 9.00h"

### Grouped by Date
When grouped by "Date":
- Each date with events
- Number of events that day
- Total hours of events

## Using Event Reports

Event reports help you:
- **Identify busy periods** - See which times have the most events
- **Track event types** - Understand what kind of events are most common
- **Plan resources** - Use historical data to predict future needs
- **Measure utilization** - See total event hours per week/month

## Exporting

Event data is included when exporting reports to CSV or PDF format.
    `
  },

  // Reports
  "viewing-reports": {
    title: "Viewing Reports",
    content: `
# Viewing Reports

Access detailed insights about your scheduling and labor costs.

## Accessing Reports

Click **Reports** in the navigation bar.

## Available Reports

### Weekly Summary
- Total shifts scheduled
- Total hours worked
- Labor costs breakdown
- Per-employee statistics

### Cost Analysis
- Daily cost trends
- Employee cost comparison
- Overtime tracking

## Filtering Reports

Use the controls to:
- Select date ranges
- Filter by employee
- Choose specific metrics

## Understanding the Data

Reports show:
- **Worked hours** - Actual shift time minus unpaid breaks
- **Labor cost** - Hours × hourly rate
- **Paid absences** - Cost of paid time off
    `
  },
  "exporting-data": {
    title: "Exporting Data",
    content: `
# Exporting Data

Export your scheduling data for payroll, records, or analysis.

## Export Formats

- **CSV** - For spreadsheets and payroll systems
- **PDF** - For printing and sharing

## How to Export

1. Go to the **Reports** page
2. Select your date range
3. Click **Export CSV** or **Export PDF**
4. The file downloads to your computer

## What's Included

Exports include:
- Employee names
- Shift dates and times
- Hours worked
- Break times
- Pay calculations
- Absence records

## Using Exports

Common uses:
- Import into payroll software
- Submit to accountants
- Keep for compliance records
- Analyze in Excel/Sheets
    `
  },
  "cost-tracking": {
    title: "Cost Tracking",
    content: `
# Cost Tracking

Monitor your labor costs in real-time as you build schedules.

## Real-Time Calculations

As you create shifts, Rotalie instantly shows:
- Per-shift cost
- Daily total cost
- Weekly projections

## Where to See Costs

### Day View
- Stats column shows per-employee cost
- Bottom summary shows daily total

### Week View
- Cell costs for each day
- Weekly totals per employee

### Add Shift Dialog
- Estimated cost shown before saving

## Multiple Pay Rates

If using split shifts with different rates:
- Each segment calculates separately
- Total combines all segments

## Cost Alerts

Coming soon: Set budget alerts to be notified when costs exceed thresholds.
    `
  },

  // Settings
  "business-hours": {
    title: "Business Hours",
    image: "/docs-images/business-hours-page.png",
    content: `
# Business Hours

Configure when your business operates to control the scheduling timeline.

![Business Hours Page](/docs-images/business-hours-page.png)

## Accessing Business Hours

Business Hours now has its own dedicated page:
1. Click **Hours** in the main navigation
2. Or go to **Hours** tab directly from the dashboard

## Setting Regular Hours

1. Go to the **Hours** page
2. For each day of the week, set:
   - **Open/Closed** toggle
   - **Opening time**
   - **Closing time**
3. Use **Apply to all** to copy times from one day to all others
4. Click **Save Changes**

## Closed Days

For days your business is closed:
- Toggle the day to "Closed"
- No shifts can be created on closed days
- The day view shows "Closed - No shifts available"

## Hours Overrides

Create special hours for holidays and events:
1. Go to the **Hours** page
2. Scroll to **Hours Overrides** section
3. Click **Add Override**
4. Choose override type:
   - **Single Date** - One specific day (e.g., Christmas Day)
   - **Date Range** - Multiple days (e.g., School Holidays)
   - **Recurring Annual** - Same day every year
5. Set whether you're closed or have modified hours
6. Click **Add Override**

## Impact on Scheduling

Business hours affect:
- Timeline display range
- Shift time constraints
- Overtime calculations

## Tips

- Set hours slightly wider than actual operation to allow for early/late shifts
- Use consistent hours for easier scheduling
- Add recurring overrides for annual holidays at the start of each year
    `
  },
  "currency-settings": {
    title: "Currency & Localization",
    content: `
# Currency & Localization

Configure currency and regional settings for your business.

## Setting Your Currency

1. Go to **Settings**
2. Find **Currency** dropdown
3. Select your currency:
   - GBP (£) - British Pounds
   - EUR (€) - Euros
   - USD ($) - US Dollars
4. Click **Save Changes**

## How Currency Affects the App

Your selected currency is used for:
- Displaying pay rates
- Cost calculations
- Reports and exports
- Invoice displays

## Automatic Detection

When you sign up, Rotalie detects your location and suggests the appropriate currency. You can change this anytime.
    `
  },
  "snap-intervals": {
    title: "Time Snap Intervals",
    content: `
# Time Snap Intervals

Control how times are rounded when creating and editing shifts.

## What is Snap Interval?

The snap interval determines how times are rounded:
- **15 minutes** - Times snap to :00, :15, :30, :45
- **30 minutes** - Times snap to :00, :30
- **5 minutes** - More precise control

## Changing Snap Interval

1. Go to **Settings**
2. Find **Snap Interval**
3. Select from:
   - 5 minutes
   - 10 minutes
   - 15 minutes (default)
   - 30 minutes
   - 60 minutes
4. Click **Save Changes**

## Benefits

- **Consistency** - All shifts align to the same intervals
- **Speed** - Faster to select times from fewer options
- **Clarity** - Cleaner timelines without odd minutes
    `
  },

  // Billing
  "subscription-plans": {
    title: "Subscription Plans",
    content: `
# Subscription Plans

Choose the plan that's right for your business.

## Available Plans

### Light - £9/month
- Up to 10 employees
- Day & week views
- Basic reports
- Email support

### Standard - £29/month (Most Popular)
- Up to 25 employees
- Advanced reports & exports
- Cost tracking
- Break management
- Absence tracking
- Priority support

### Professional - £59/month
- Unlimited employees
- All Standard features
- Multiple locations
- Dedicated support

## Free Trial

All new accounts get a **30-day free trial** with full access to Professional features. No credit card required.

## Need More Employees?

Purchase additional employee packs:
- +10 employees for £10/month
    `
  },
  "managing-subscription": {
    title: "Managing Your Subscription",
    content: `
# Managing Your Subscription

Easily manage your Rotalie subscription from the Billing page.

## Accessing Billing

Click **Billing** in the navigation bar.

## Viewing Your Plan

The Billing page shows:
- Current plan name and price
- Next billing date
- Payment method on file

## Upgrading Your Plan

1. Go to **Billing**
2. Click **Upgrade Plan**
3. Select your new plan
4. Complete checkout
5. Changes apply immediately

## Downgrading

1. Click **Manage Subscription**
2. Select a lower plan
3. Confirm the change
4. Takes effect at next billing cycle

## Cancellation

1. Click **Manage Subscription**
2. Select **Cancel**
3. Confirm cancellation
4. Access continues until billing period ends
    `
  },
  "invoices": {
    title: "Invoices & Payment History",
    content: `
# Invoices & Payment History

Access your billing history and download invoices.

## Viewing Payment History

1. Go to **Billing**
2. Scroll to **Payment History**
3. See all past payments with:
   - Date
   - Amount
   - Status

## Downloading Invoices

1. Find the payment in history
2. Click the **Download** icon
3. PDF invoice downloads to your device

## Invoice Details

Each invoice includes:
- Invoice number
- Payment date
- Amount paid
- Plan details
- Your company information

## Failed Payments

If a payment fails:
- You'll receive an email notification
- Update your payment method in Billing
- The system will retry automatically
    `
  },
  
  // Account Settings
  "my-account": {
    title: "My Account",
    content: `
# My Account

Access your account settings from the user menu in the top-right corner of the dashboard.

## Accessing Account Settings

1. Click the **user icon** in the top-right corner
2. Select **My Account** from the dropdown
3. You'll see your profile and password settings

## What You Can Manage

From My Account, you can:
- Update your name
- Change your company name
- View your company code
- Change your password

The account page is divided into two sections:
- **Profile Information** - Your personal and company details
- **Change Password** - Update your login password
    `
  },
  "company-details": {
    title: "Company Details",
    content: `
# Company Details

Manage your company information from the My Account page.

## Updating Company Name

1. Go to **My Account** (from user menu)
2. Find the **Company Name** field
3. Enter your new company name
4. Click **Save Changes**

## Your Company Code

Your company code is displayed in the Profile Information section. This code:
- Is used by staff members to log in
- Cannot be changed
- Should be shared only with authorized employees

## Email Address

Your admin email address:
- Is shown but cannot be changed
- Is used for all account notifications
- Receives billing and security alerts
    `
  },
  "change-password": {
    title: "Changing Your Password",
    content: `
# Changing Your Password

Keep your account secure by updating your password regularly.

## How to Change Your Password

1. Click the **user icon** in the top-right corner
2. Select **My Account**
3. Scroll to **Change Password** section
4. Enter your **current password**
5. Enter your **new password**
6. Confirm your new password
7. Click **Change Password**

## Password Requirements

Your new password must:
- Be at least 6 characters long
- Be different from your current password

## Troubleshooting

**"Current password is incorrect"**
- Double-check your current password
- Use the **Forgot Password** link on login if needed

**Password not updating**
- Ensure new passwords match exactly
- Check password meets minimum requirements
    `
  },
};

const Documentation = () => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeArticle, setActiveArticle] = useState("introduction");

  // Parse article from URL hash
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && docContent[hash]) {
      setActiveArticle(hash);
    }
  }, [location.hash]);

  // Get current article content
  const currentContent = docContent[activeArticle] || docContent["introduction"];
  
  // Find current section
  const currentSection = docSections.find(section => 
    section.articles.some(a => a.id === activeArticle)
  );

  // Filter articles by search
  const filteredSections = searchQuery
    ? docSections.map(section => ({
        ...section,
        articles: section.articles.filter(article =>
          article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (docContent[article.id]?.content || "").toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(section => section.articles.length > 0)
    : docSections;

  // Simple markdown-like rendering
  const renderContent = (content) => {
    const lines = content.trim().split('\n');
    const elements = [];
    let inList = false;
    let listItems = [];

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} className="list-disc list-inside space-y-2 my-4 text-slate-600">
            {listItems.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        );
        listItems = [];
      }
      inList = false;
    };

    lines.forEach((line, index) => {
      const trimmed = line.trim();
      
      // Check for markdown image: ![alt](src)
      const imageMatch = trimmed.match(/^!\[(.*?)\]\((.*?)\)$/);
      if (imageMatch) {
        flushList();
        const [, alt, src] = imageMatch;
        elements.push(
          <div key={index} className="my-6 rounded-lg overflow-hidden border border-slate-200 shadow-sm">
            <img 
              src={src} 
              alt={alt} 
              className="w-full h-auto"
              loading="lazy"
            />
          </div>
        );
        return;
      }
      
      if (trimmed.startsWith('# ')) {
        flushList();
        elements.push(
          <h1 key={index} className="text-3xl font-bold text-slate-900 mb-6 mt-8 first:mt-0">
            {trimmed.slice(2)}
          </h1>
        );
      } else if (trimmed.startsWith('## ')) {
        flushList();
        elements.push(
          <h2 key={index} className="text-2xl font-semibold text-slate-900 mb-4 mt-8">
            {trimmed.slice(3)}
          </h2>
        );
      } else if (trimmed.startsWith('### ')) {
        flushList();
        elements.push(
          <h3 key={index} className="text-xl font-semibold text-slate-900 mb-3 mt-6">
            {trimmed.slice(4)}
          </h3>
        );
      } else if (trimmed.startsWith('- ')) {
        inList = true;
        // Parse bold text within list items
        const itemContent = trimmed.slice(2).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        listItems.push(<span dangerouslySetInnerHTML={{ __html: itemContent }} />);
      } else if (trimmed.startsWith('1. ') || trimmed.match(/^\d+\./)) {
        // Numbered list
        if (!inList || listItems.length === 0) {
          flushList();
          inList = true;
        }
        const itemContent = trimmed.replace(/^\d+\.\s*/, '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        listItems.push(<span dangerouslySetInnerHTML={{ __html: itemContent }} />);
      } else if (trimmed === '') {
        flushList();
      } else {
        flushList();
        // Parse inline formatting
        let formatted = trimmed
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.*?)\*/g, '<em>$1</em>')
          .replace(/`(.*?)`/g, '<code class="bg-slate-100 px-1.5 py-0.5 rounded text-sm font-mono text-violet-600">$1</code>')
          .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-violet-600 hover:text-violet-700 underline">$1</a>');
        
        elements.push(
          <p key={index} className="text-slate-600 leading-relaxed my-4" dangerouslySetInnerHTML={{ __html: formatted }} />
        );
      }
    });

    flushList();
    return elements;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 h-16 bg-white border-b border-slate-200">
        <div className="h-full px-4 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden p-2 -ml-2 text-slate-600 hover:text-slate-900"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Link to="/" className="flex items-center gap-2">
              <RotalieLogo size="small" />
            </Link>
            <span className="text-slate-300">|</span>
            <span className="font-semibold text-slate-900">Documentation</span>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden md:flex relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="search"
                placeholder="Search docs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-64 h-9 bg-slate-50 border-slate-200"
              />
            </div>
            <Link to="/dashboard">
              <Button variant="outline" size="sm" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back to App
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={cn(
          "fixed lg:sticky top-16 left-0 z-40 w-72 h-[calc(100vh-4rem)] bg-white border-r border-slate-200 overflow-y-auto transition-transform lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}>
          <nav className="p-4 space-y-6">
            {/* Mobile search */}
            <div className="lg:hidden relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="search"
                placeholder="Search docs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-full h-9 bg-slate-50 border-slate-200"
              />
            </div>

            {filteredSections.map((section) => (
              <div key={section.id}>
                <div className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-slate-900">
                  <section.icon className="w-4 h-4 text-slate-500" />
                  {section.title}
                </div>
                <ul className="mt-1 space-y-1">
                  {section.articles.map((article) => (
                    <li key={article.id}>
                      <button
                        onClick={() => {
                          setActiveArticle(article.id);
                          setSidebarOpen(false);
                          window.location.hash = article.id;
                        }}
                        className={cn(
                          "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
                          activeArticle === article.id
                            ? "bg-violet-50 text-violet-700 font-medium"
                            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                        )}
                      >
                        {article.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        {/* Backdrop for mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/20 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          <div className="max-w-3xl mx-auto px-6 lg:px-12 py-12">
            {/* Breadcrumb */}
            {currentSection && (
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-8">
                <Link to="/documentation" className="hover:text-slate-700">Docs</Link>
                <ChevronRight className="w-4 h-4" />
                <span>{currentSection.title}</span>
                <ChevronRight className="w-4 h-4" />
                <span className="text-slate-900">{currentContent.title}</span>
              </div>
            )}

            {/* Article Content */}
            <article className="prose prose-slate max-w-none">
              {renderContent(currentContent.content)}
            </article>

            {/* Navigation */}
            <div className="mt-16 pt-8 border-t border-slate-200">
              <div className="flex justify-between">
                {/* Previous article */}
                {(() => {
                  let prevArticle = null;
                  let prevSection = null;
                  outer: for (const section of docSections) {
                    for (let i = 0; i < section.articles.length; i++) {
                      if (section.articles[i].id === activeArticle) {
                        if (i > 0) {
                          prevArticle = section.articles[i - 1];
                          prevSection = section;
                        } else {
                          const sectionIndex = docSections.indexOf(section);
                          if (sectionIndex > 0) {
                            const prev = docSections[sectionIndex - 1];
                            prevArticle = prev.articles[prev.articles.length - 1];
                            prevSection = prev;
                          }
                        }
                        break outer;
                      }
                    }
                  }
                  return prevArticle ? (
                    <button
                      onClick={() => {
                        setActiveArticle(prevArticle.id);
                        window.location.hash = prevArticle.id;
                      }}
                      className="flex items-center gap-2 text-slate-600 hover:text-violet-600 transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span className="text-sm">{prevArticle.title}</span>
                    </button>
                  ) : <div />;
                })()}

                {/* Next article */}
                {(() => {
                  let nextArticle = null;
                  let nextSection = null;
                  outer: for (const section of docSections) {
                    for (let i = 0; i < section.articles.length; i++) {
                      if (section.articles[i].id === activeArticle) {
                        if (i < section.articles.length - 1) {
                          nextArticle = section.articles[i + 1];
                          nextSection = section;
                        } else {
                          const sectionIndex = docSections.indexOf(section);
                          if (sectionIndex < docSections.length - 1) {
                            const next = docSections[sectionIndex + 1];
                            nextArticle = next.articles[0];
                            nextSection = next;
                          }
                        }
                        break outer;
                      }
                    }
                  }
                  return nextArticle ? (
                    <button
                      onClick={() => {
                        setActiveArticle(nextArticle.id);
                        window.location.hash = nextArticle.id;
                      }}
                      className="flex items-center gap-2 text-slate-600 hover:text-violet-600 transition-colors"
                    >
                      <span className="text-sm">{nextArticle.title}</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  ) : <div />;
                })()}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Documentation;
